'use client';

import { useEffect, useRef, useState } from 'react';

type Props = {
  children: React.ReactNode;
  /** Jeda kemunculan dalam milidetik — untuk efek berurutan pada grid kartu. */
  delay?: number;
  className?: string;
  as?: 'div' | 'li' | 'section';
};

/**
 * Membuat isinya muncul (fade + naik) saat pertama kali masuk viewport.
 * Animasinya sendiri didefinisikan di globals.css (`.reveal` / `.reveal-in`),
 * dan otomatis mati bila pengguna memilih `prefers-reduced-motion`.
 */
export default function Reveal({ children, delay = 0, className = '', as = 'div' }: Props) {
  const ref = useRef<HTMLElement | null>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Browser lawas tanpa IntersectionObserver: tampilkan langsung.
    if (typeof IntersectionObserver === 'undefined') {
      setShown(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setShown(true);
            observer.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const Tag = as as 'div';

  return (
    <Tag
      ref={ref as React.RefObject<HTMLDivElement>}
      className={`reveal ${shown ? 'reveal-in' : ''} ${className}`}
      style={delay ? { transitionDelay: `${delay}ms` } : undefined}
    >
      {children}
    </Tag>
  );
}
